import { grey, blue } from '@mui/material/colors';
import { createTheme } from '@mui/material';
import { loadImage, prepareIcon } from './mapUtil';

import directionSvg from '../../resources/images/direction.svg';
import backgroundSvg from '../../resources/images/background.svg';
import animalSvg from '../../resources/images/icon/animal.svg';
import bicycleSvg from '../../resources/images/icon/bicycle.svg';
import boatSvg from '../../resources/images/icon/boat.svg';
import busSvg from '../../resources/images/icon/bus.svg';
import carSvg from '../../resources/images/icon/car.svg';
import camperSvg from '../../resources/images/icon/camper.svg';
import craneSvg from '../../resources/images/icon/crane.svg';
import defaultSvg from '../../resources/images/icon/default.svg';
import startSvg from '../../resources/images/icon/start.svg';
import finishSvg from '../../resources/images/icon/finish.svg';
import helicopterSvg from '../../resources/images/icon/helicopter.svg';
import motorcycleSvg from '../../resources/images/icon/motorcycle.svg';
import personSvg from '../../resources/images/icon/person.svg';
import planeSvg from '../../resources/images/icon/plane.svg';
import scooterSvg from '../../resources/images/icon/scooter.svg';
import shipSvg from '../../resources/images/icon/ship.svg';
import tractorSvg from '../../resources/images/icon/tractor.svg';
import trailerSvg from '../../resources/images/icon/trailer.svg';
import trainSvg from '../../resources/images/icon/train.svg';
import tramSvg from '../../resources/images/icon/tram.svg';
import truckSvg from '../../resources/images/icon/truck.svg';
import vanSvg from '../../resources/images/icon/van.svg';
import pickupSvg from '../../resources/images/icon/pickup.svg';
import shoppingBagIconSvg from '../../resources/images/icon/shoppingbag.svg';
import tagSvg from '../../resources/images/icon/tag.svg';
import load3dImage from './icons3d';

// Cluster icons (estilo GPSWOX)
import clusterSmallSvg from '../../resources/images/cluster/cluster-small.svg';
import clusterMediumSvg from '../../resources/images/cluster/cluster-medium.svg';
import clusterLargeSvg from '../../resources/images/cluster/cluster-large.svg';
import clusterXlargeSvg from '../../resources/images/cluster/cluster-xlarge.svg';

export const mapIcons = {
  animal: animalSvg,
  bicycle: bicycleSvg,
  boat: boatSvg,
  bus: busSvg,
  car: carSvg,
  camper: camperSvg,
  crane: craneSvg,
  default: defaultSvg,
  finish: finishSvg,
  helicopter: helicopterSvg,
  motorcycle: motorcycleSvg,
  person: personSvg,
  plane: planeSvg,
  scooter: scooterSvg,
  ship: shipSvg,
  start: startSvg,
  tractor: tractorSvg,
  trailer: trailerSvg,
  trailer2: trailerSvg,
  train: trainSvg,
  tram: tramSvg,
  truck: truckSvg,
  van: vanSvg,
  pickup: pickupSvg,
  shoppingbag: shoppingBagIconSvg,
  tag: tagSvg,
};

export const mapIconKey = (category) => (mapIcons.hasOwnProperty(category) ? category : 'default');

export const mapImages = {};

const theme = createTheme({
  palette: {
    neutral: { main: grey[500] },
    info: { main: blue[600] },  // Azul para dispositivos em movimento
  },
});

// Ícones 3D locais (carregamento rápido) - todos os convertidos de PHP para JS
const local3dIcons = [
  'car', 'default', 'truck', 'motorcycle', 'pickup', 'bus',
  'van', 'tractor', 'boat', 'bicycle', 'offroad', 'trailer', 'scooter', 'tag'
];

// Cores para pré-carregar ícones 3D
const preloadColors = ['info', 'success', 'error', 'neutral'];

export default async () => {
  const background = await loadImage(backgroundSvg);
  mapImages.background = await prepareIcon(background);
  mapImages.direction = await prepareIcon(await loadImage(directionSvg));

  // Carregar ícones de cluster (estilo GPSWOX)
  mapImages['cluster-small'] = await prepareIcon(await loadImage(clusterSmallSvg));
  mapImages['cluster-medium'] = await prepareIcon(await loadImage(clusterMediumSvg));
  mapImages['cluster-large'] = await prepareIcon(await loadImage(clusterLargeSvg));
  mapImages['cluster-xlarge'] = await prepareIcon(await loadImage(clusterXlargeSvg));

  // Pré-carregar ícones 2D (fallback)
  await Promise.all(Object.keys(mapIcons).map(async (category) => {
    const results = [];
    ['info', 'success', 'error', 'neutral'].forEach((color) => {
      results.push(loadImage(mapIcons[category]).then((icon) => {
        mapImages[`${category}-${color}`] = prepareIcon(background, icon, theme.palette[color].main);
      }));
    });
    await Promise.all(results);
  }));

  // Pré-carregar apenas rotações principais (0, 90, 180, 270) para startup rápido
  // Outras rotações serão carregadas sob demanda pelo icons3d/index.js
  const mainRotations = [0, 90, 180, 270];

  // Carregar ícones 3D mais comuns primeiro (car, truck, motorcycle)
  const priorityIcons = ['car', 'default', 'truck', 'motorcycle'];
  const otherIcons = local3dIcons.filter(i => !priorityIcons.includes(i));

  // Prioridade alta: ícones mais usados com rotações principais
  await Promise.all(
    priorityIcons.flatMap((icon) =>
      preloadColors.flatMap((color) =>
        mainRotations.map((rotation) => loadId(icon, color, rotation))
      )
    )
  );

  // Carregar outros ícones em background (não bloqueia)
  Promise.all(
    otherIcons.flatMap((icon) =>
      preloadColors.flatMap((color) =>
        mainRotations.map((rotation) => loadId(icon, color, rotation))
      )
    )
  ).catch(() => {}); // Silenciar erros
};

async function loadId(icon, color, rotation) {
  const id = `${icon}-${color}-${rotation}`;
  try {
    mapImages[id] = await load3dImage({ id }, false);
  } catch (err) {
    // Silenciar erros de preload - fallback será usado
  }
}
